import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import CustomBottomNavigation from './components/BottomNavigation'
import { useScrollRestoration } from './hooks/useScrollRestoration'

// Lazy load pages for better performance
import { lazy, Suspense, ReactNode } from 'react'

const HomePage = lazy(() => import('./pages/HomePage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const CodePage = lazy(() => import('./pages/CodePage'))
const CustomPage = lazy(() => import('./pages/CustomPage'))
const ContactAdminPage = lazy(() => import('./pages/ContactAdminPage'))
const StockOnePage = lazy(() => import('./pages/StockOnePage'))
const StockTwoPage = lazy(() => import('./pages/StockTwoPage'))
const StockThreePage = lazy(() => import('./pages/StockThreePage'))
const StockFourPage = lazy(() => import('./pages/StockFourPage'))
const StockFivePage = lazy(() => import('./pages/StockFivePage'))
const ResultPage = lazy(() => import('./pages/ResultPage'))
const FormulaPage = lazy(() => import('./pages/FormulaPage'))
const RegisterWebPage = lazy(() => import('./pages/RegisterWebPage'))

// Protected Route component
interface ProtectedRouteProps {
  children: ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public Route component (redirect to home if authenticated)
const PublicRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return null
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Layout wrapper with bottom navigation
interface LayoutWrapperProps {
  children: ReactNode
  showBottomNav?: boolean
}

const LayoutWrapper = ({ children, showBottomNav = true }: LayoutWrapperProps) => (
  <Layout>
    {children}
    {showBottomNav && <CustomBottomNavigation />}
  </Layout>
)

function AppRoutes() {
  // Remember and restore scroll position across back/forward on every page.
  useScrollRestoration()

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LayoutWrapper showBottomNav={false}>
              <Suspense fallback={null}>
                <LoginPage />
              </Suspense>
            </LayoutWrapper>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <LayoutWrapper showBottomNav={false}>
              <Suspense fallback={null}>
                <RegisterPage />
              </Suspense>
            </LayoutWrapper>
          </PublicRoute>
        }
      />
      <Route
        path="/contact-admin"
        element={
          <LayoutWrapper showBottomNav={false}>
            <Suspense fallback={null}>
              <ContactAdminPage />
            </Suspense>
          </LayoutWrapper>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <HomePage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/code"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <CodePage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom/*"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <CustomPage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/one"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <StockOnePage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/two"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <StockTwoPage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/three"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <StockThreePage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/four"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <StockFourPage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/five"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <StockFivePage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/result/:name"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <ResultPage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/formula/*"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <FormulaPage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/register-web"
        element={
          <ProtectedRoute>
            <LayoutWrapper>
              <Suspense fallback={null}>
                <RegisterWebPage />
              </Suspense>
            </LayoutWrapper>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
